import React from "react";
import { Box, Card, Heading, Icon, Stack, Text } from "@chakra-ui/react";

export const SectionShell = ({ children, bg = "public.bg.canvas", color = "public.fg.default", ...props }) => (
  <Box as="section" bg={bg} color={color} px={{ base: 5, md: 10 }} py={{ base: 16, md: 24 }} {...props}>
    <Box maxW="7xl" mx="auto">
      {children}
    </Box>
  </Box>
);

export const SectionEyebrow = ({ children, tone = "brand", alignSelf = "start" }) => {
  const isDark = tone === "dark";
  const isStatus = tone === "status";

  return (
    <Text
      alignSelf={alignSelf}
      px="4"
      py="1.5"
      rounded="full"
      bg={isStatus ? "red.700" : isDark ? "whiteAlpha.200" : "public.bg.elevated"}
      color={isStatus || isDark ? "white" : "public.fg.default"}
      borderWidth="1px"
      borderColor={isStatus ? "red.800" : isDark ? "whiteAlpha.300" : "public.border.default"}
      letterSpacing="wide"
      fontSize="xs"
      fontWeight="800"
      textTransform="uppercase"
    >
      {!isStatus && !isDark && <Box as="span" display="inline-block" boxSize="7px" mr="2" rounded="full" bg="brand.red" />}
      {children}
    </Text>
  );
};

export const SectionHeading = ({ eyebrow, title, description, align = "start", tone = "brand" }) => (
  <Stack gap="4" align={align} textAlign={align === "center" ? "center" : "start"}>
    <SectionEyebrow tone={tone} alignSelf={align === "center" ? "center" : "start"}>{eyebrow}</SectionEyebrow>
    <Heading
      as="h2"
      fontFamily="display"
      fontSize={{ base: "3xl", md: "5xl" }}
      lineHeight="1.02"
      maxW="3xl"
    >
      {title}
    </Heading>
    {description ? (
      <Text color={tone === "dark" ? "whiteAlpha.800" : "public.fg.muted"} fontSize={{ base: "md", md: "lg" }} lineHeight="1.8" maxW="3xl">
        {description}
      </Text>
    ) : null}
  </Stack>
);

export const FloatingStat = ({ icon, value, label, ...props }) => (
  <Card.Root
    bg="public.bg.elevated"
    borderWidth="1px"
    borderColor="public.border.default"
    rounded="18px"
    shadow="cardLift"
    {...props}
  >
    <Card.Body p="4">
      <Stack direction="row" gap="3" align="center">
        <Box display="grid" placeItems="center" boxSize="11" rounded="14px" bg="brand.redSubtle" color="brand.redFg">
          <Icon as={icon} boxSize="5" />
        </Box>
        <Stack gap="0">
          <Text fontFamily="display" fontSize="xl" fontWeight="700" color="public.fg.default" lineHeight="1">
            {value}
          </Text>
          <Text fontSize="xs" color="public.fg.muted" fontWeight="700">
            {label}
          </Text>
        </Stack>
      </Stack>
    </Card.Body>
  </Card.Root>
);

export const ImageStoryCard = ({ image, title, subtitle, accent = "brand.red", position = "center" }) => (
  <Card.Root
    role="group"
    overflow="hidden"
    rounded="32px 14px 32px 14px"
    borderWidth="1px"
    borderColor="public.border.default"
    bg="public.bg.surface"
    minH={{ base: "300px", md: "430px" }}
    transition="transform 220ms ease, box-shadow 220ms ease"
    _hover={{ transform: "translateY(-6px)", shadow: "cardLift" }}
  >
    <Box position="relative" minH="inherit" overflow="hidden">
      <Box
        position="absolute"
        inset="0"
        bgImage={`url(${image})`}
        bgSize="cover"
        bgPosition={position}
        transition="transform 500ms ease"
        _groupHover={{ transform: "scale(1.07)" }}
      />
      <Box position="absolute" inset="0" bg="linear-gradient(to top, rgba(10,0,0,.78), rgba(0,0,0,0.08) 58%)" />
      <Stack position="absolute" left="6" right="6" bottom="6" gap="1">
        <Text fontFamily="display" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="700" color={accent} lineHeight="1">
          {title}
        </Text>
        <Text color="whiteAlpha.900" fontSize="sm" fontWeight="800" textTransform="uppercase">
          {subtitle}
        </Text>
      </Stack>
      <Box position="absolute" top="5" right="5" boxSize="3" rounded="full" bg={accent} boxShadow={`0 0 0 5px color-mix(in srgb, currentColor 22%, transparent)`} />
    </Box>
  </Card.Root>
);

export const BenefitCard = ({ icon, title, description }) => (
  <Card.Root bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.default" rounded="24px" h="full">
    <Card.Body p="6" gap="4">
      <Box display="grid" placeItems="center" boxSize="12" rounded="16px" bg="brand.redSubtle" color="brand.redFg">
        <Icon as={icon} boxSize="6" />
      </Box>
      <Stack gap="2">
        <Heading as="h3" fontFamily="display" fontSize="xl">
          {title}
        </Heading>
        <Text color="public.fg.muted" lineHeight="1.7">
          {description}
        </Text>
      </Stack>
    </Card.Body>
  </Card.Root>
);
